/**
 * Generic filterable column header for DataTable columns.
 * Renders the header label with an optional dropdown filter menu.
 *
 * @module features/tasks/components/FilterableColumnHeader
 */

import React from 'react';
import { CaretDown, Check } from '@phosphor-icons/react';

interface FilterOption {
  id: string;
  label: string;
}

interface FilterableColumnHeaderProps {
  label: string;
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
}

export const FilterableColumnHeader: React.FC<FilterableColumnHeaderProps> = ({
  label,
  options,
  value,
  onChange,
}) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const selectedLabel = options.find((o) => o.id === value)?.label;

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {label && <span>{label}</span>}
        {selectedLabel && (
          <span className="text-foreground/70">{label ? `· ${selectedLabel}` : selectedLabel}</span>
        )}
        <CaretDown size={12} className="shrink-0" />
      </button>

      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 min-w-[160px] rounded-md border bg-popover p-1 shadow-md">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                onChange(option.id);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Check
                size={12}
                className={`shrink-0 ${value === option.id ? 'opacity-100' : 'opacity-0'}`}
              />
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
